/**
 * Hooks de gestão dos participantes de um Bond (ACL, ADR-130).
 *
 * Complementa o `useBondParticipants` read-only de `use-bond.ts` (contagem da aba
 * Cliente): aqui a listagem aceita `includeRevoked` e há os 3 mutadores (add /
 * update / revoke). Mutações usam optimistic-lock por `version` (If-Match, ADR-071)
 * e invalidam a chave de lista — o prefixo `['bond-participants', bondId]` casa
 * tanto a contagem da aba quanto a lista do drawer (incl. includeRevoked).
 *
 * Gate `bond-basic` (server-side em cada endpoint). 403 → toast via api-client.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  api,
  type AddParticipantRequest,
  type BondParticipantWireItem,
  type UpdateParticipantRequest,
} from '../lib/api-client';

const KEY = 'bond-participants';

export function useManagedParticipants(
  bondId: string | null,
  includeRevoked: boolean,
): {
  data: BondParticipantWireItem[];
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery({
    queryKey: [KEY, bondId, { includeRevoked }],
    queryFn: async () => {
      if (bondId === null) throw new Error('bondId required');
      const res = await api.listBondParticipants(bondId, { includeRevoked });
      return res.data;
    },
    enabled: bondId !== null,
    staleTime: 30_000,
  });
  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

function useInvalidateParticipants(bondId: string | null): () => void {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: [KEY, bondId] });
}

export function useAddParticipant(
  bondId: string | null,
): ReturnType<typeof useMutation<BondParticipantWireItem, Error, AddParticipantRequest>> {
  const invalidate = useInvalidateParticipants(bondId);
  return useMutation<BondParticipantWireItem, Error, AddParticipantRequest>({
    mutationFn: (body) => {
      if (bondId === null) throw new Error('bondId required');
      return api.addBondParticipant(bondId, body);
    },
    onSuccess: invalidate,
  });
}

interface UpdateVars {
  participantId: string;
  version: number;
  body: UpdateParticipantRequest;
}

export function useUpdateParticipant(
  bondId: string | null,
): ReturnType<typeof useMutation<BondParticipantWireItem, Error, UpdateVars>> {
  const invalidate = useInvalidateParticipants(bondId);
  return useMutation<BondParticipantWireItem, Error, UpdateVars>({
    mutationFn: ({ participantId, version, body }) => {
      if (bondId === null) throw new Error('bondId required');
      return api.updateBondParticipant(bondId, participantId, version, body);
    },
    onSuccess: invalidate,
  });
}

interface RevokeVars {
  participantId: string;
  version: number;
  reason?: string;
}

export function useRevokeParticipant(
  bondId: string | null,
): ReturnType<typeof useMutation<BondParticipantWireItem, Error, RevokeVars>> {
  const invalidate = useInvalidateParticipants(bondId);
  return useMutation<BondParticipantWireItem, Error, RevokeVars>({
    mutationFn: ({ participantId, version, reason }) => {
      if (bondId === null) throw new Error('bondId required');
      return api.revokeBondParticipant(bondId, participantId, version, reason);
    },
    onSuccess: invalidate,
  });
}
